// src/pages/markets/board/card/cardFormat.ts
//
// Same conventions as ConvictionScreenerView / WatchlistView, so a number means
// the same thing on both screens.

import type { MetricDef, MetricFormat } from '../api/metrics';

export const EM_DASH = '—';

export function formatUsd(v: number | null | undefined): string {
    if (v == null || !Number.isFinite(v)) return EM_DASH;
    const sign = v < 0 ? '-' : '';
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
    return `${sign}$${abs.toFixed(0)}`;
}

export function formatCount(v: number | null | undefined): string {
    if (v == null || !Number.isFinite(v)) return EM_DASH;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
    return String(Math.round(v));
}

export function formatProb(v: number | null | undefined): string {
    if (v == null || !Number.isFinite(v)) return EM_DASH;
    return `${(v * 100).toFixed(0)}%`;
}

export function formatProbPoints(v: number | null | undefined, signed: boolean): string {
    if (v == null || !Number.isFinite(v)) return EM_DASH;
    const pts = v * 100;
    if (!signed) return `${Math.abs(pts).toFixed(1)}pp`;
    return `${pts >= 0 ? '+' : ''}${pts.toFixed(1)}pp`;
}

export function formatRatio(v: number | null | undefined): string {
    if (v == null || !Number.isFinite(v)) return EM_DASH;
    return v.toFixed(2);
}

export function formatNumber(v: number | null | undefined): string {
    if (v == null || !Number.isFinite(v)) return EM_DASH;
    return v.toLocaleString();
}

// An unknown format falls back to toLocaleString rather than throwing, so a
// metric key the backend adds tomorrow renders as a plain number today.
export function formatByKind(
    v: number | null | undefined,
    format: MetricFormat | string,
    signed = false,
): string {
    switch (format) {
        case 'usd':
            return formatUsd(v);
        case 'prob':
            return formatProb(v);
        case 'prob_points':
            return formatProbPoints(v, signed);
        case 'prob_diff':
            return formatProbDiff(v, signed);
        case 'ratio':
            return formatRatio(v);
        case 'count':
            return formatCount(v);
        default:
            return formatNumber(v);
    }
}

export function formatMetric(def: MetricDef, v: number | null | undefined): string {
    return formatByKind(v, def.format, def.signed);
}

export function formatVolume(v: number | null | undefined): string {
    return formatUsd(v);
}

export function ageSeconds(ts: string | null | undefined, now: number = Date.now()): number | null {
    if (!ts) return null;
    const t = new Date(ts).getTime();
    if (!Number.isFinite(t)) return null;
    return Math.max(0, Math.floor((now - t) / 1000));
}

export function formatAge(ts: string | null | undefined, now: number = Date.now()): string {
    const secs = ageSeconds(ts, now);
    if (secs == null) return EM_DASH;
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

export function formatDuration(seconds: number | null | undefined): string {
    if (seconds == null || !Number.isFinite(seconds)) return EM_DASH;
    const s = Math.max(0, Math.floor(seconds));
    if (s < 60) return `${s}s`;
    const mins = Math.floor(s / 60);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
}

export function shortAddress(addr: string | null | undefined): string {
    if (!addr) return EM_DASH;
    if (addr.length <= 12) return addr;
    return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

export const PAST_END = 'past est. end';

export function formatTimeLeft(endDate: string | null | undefined, now: number = Date.now()): string {
    if (!endDate) return EM_DASH;
    const t = new Date(endDate).getTime();
    if (!Number.isFinite(t)) return EM_DASH;
    const diff = t - now;
    // Not "ended": end_date is Polymarket's estimate and open markets trade past
    // it. Closed events show their status label instead of this.
    if (diff <= 0) return PAST_END;
    const mins = Math.floor(diff / 60_000);
    if (mins < 60) return `in ${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 48) return `in ${hrs}h`;
    return `in ${Math.floor(hrs / 24)}d`;
}

export function truncate(s: string | null | undefined, max: number): string {
    if (!s) return '';
    return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

// Difference of two probabilities. Same quantity as prob_points, shown the way
// the board reads it: a percentage, not academic percentage points.
export function formatProbDiff(v: number | null | undefined, signed = true): string {
    if (v == null || !Number.isFinite(v)) return EM_DASH;
    const pct = v * 100;
    return `${signed && pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

// Absolute creation stamp. Same-day events drop the date: in a column sorted by
// age the day repeats, and only the clock time distinguishes rows.
export function formatCreatedStamp(iso: string | null | undefined, now: number): string {
    if (!iso) return EM_DASH;
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return EM_DASH;
    const d = new Date(t);
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    const sameDay = new Date(now).toDateString() === d.toDateString();
    if (sameDay) return time;
    return `${d.toLocaleDateString([], { day: '2-digit', month: 'short' })} ${time}`;
}

export function formatCreatedDate(iso: string | null | undefined, now: number): string {
    if (!iso) return EM_DASH;
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return EM_DASH;

    const d = new Date(t);
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

    const ref = new Date(now);
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const today = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate()).getTime();
    const yesterday = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate() - 1).getTime();

    if (day === today) return `today ${time}`;
    if (day === yesterday) return `yesterday ${time}`;

    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}. ${time}`;
}

export type AgeTier = 'hot' | 'warm' | 'today' | 'old';

// Being early is measured in hours, not days — hence the buckets.
export function ageTier(iso: string | null | undefined, now: number): AgeTier {
    const s = ageSeconds(iso, now);
    if (s === null) return 'old';
    if (s < 3_600) return 'hot';
    if (s < 21_600) return 'warm';
    if (s < 86_400) return 'today';
    return 'old';
}

export function formatCents(v: number | null | undefined): string {
    if (v == null || !Number.isFinite(v)) return EM_DASH;
    return `${(v * 100).toFixed(0)}¢`;
}

export const LIVE_PRICE_FRESH_S = 86_400;

export interface PricedMarket {
    yes_prob?: number | null;
    live_prob?: number | null;
    live_prob_at?: string | null;
}

// The one place the live/synced choice is made. Time-free, so sorts and bar
// widths can call it without threading a clock through every component.
export function priceValue(m: PricedMarket | null | undefined): number | null {
    if (!m) return null;
    return m.live_prob ?? m.yes_prob ?? null;
}

export interface PriceMeta {
    value: number | null;
    live: boolean;
    fresh: boolean;
    title: string;
}

export function priceMeta(
    m: PricedMarket | null | undefined,
    now: number = Date.now(),
): PriceMeta {
    const value = priceValue(m);
    if (value === null) return { value: null, live: false, fresh: false, title: 'no price' };
    if (m?.live_prob == null) {
        return { value, live: false, fresh: false, title: 'last trade more than 24h ago' };
    }
    const secs = ageSeconds(m.live_prob_at, now);
    return {
        value,
        live: true,
        fresh: secs !== null && secs < LIVE_PRICE_FRESH_S,
        title: secs === null ? 'last price' : `last price ${formatAge(m.live_prob_at, now)}`,
    };
}